import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import TreeSelect from 'components/common/TreeSelect';
import useApi from 'helpers/api';
import { priorities, ticketOrigins } from 'helpers/utils';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import CustomerAssetsService from 'services/CustomerAssets.service';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import TypeTicketsService from 'services/TypeTickets.service';

const PropertiesTicketForm = forwardRef(({ data, handleSubmit }, ref) => {
  const api = useApi();
  const [filters] = useState({ size: 100, includeInactives: false });
  const queryParams = new URLSearchParams(filters).toString();
  const [typesTicket, setTypesTicket] = useState({ content: [] });
  const [agents, setAgents] = useState([]);
  const [serviceGroups, setServiceGroups] = useState({ content: [] });
  const [serviceCatalogs, setServiceCatalogs] = useState([
    { categories: [{ subcategories: [] }] }
  ]);
  const [assets, setAssets] = useState([]);
  const [priority, setPriority] = useState('');
  const [origin, setOrigin] = useState('');
  const [agent, setAgent] = useState({ id: '' });
  const [serviceGroup, setServiceGroup] = useState({ id: '' });
  const [serviceCatalog, setServiceCatalog] = useState({ id: '' });
  const [category, setCategory] = useState({ id: '' });
  const [subcategory, setSubcategory] = useState({ id: '' });
  const [typeTicket, setTypeTicket] = useState({ id: '' });
  const [body, setBody] = useState({});
  const form = useRef(null);
  const [showBody, setShowBody] = useState(true);
  const [searchTermAsset, setSearchTermAsset] = useState('');
  const [showAssetsSuggestions, setShowAssetsSuggestions] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    typeTicket: false,
    priority: false,
    serviceGroup: false,
    agent: false,
    serviceCatalog: false,
    origin: false
  });

  useEffect(() => {
    setBody({});
    setTypeTicket({ id: data.typeTicket ? data.typeTicket.id : '' });
    setPriority(data.priority ? data.priority.priority : '');
    setOrigin(data.origin ? data.origin?.ticketOrigin : '');
    setAgent({ id: data.agent ? data.agent.id : '' });
    setServiceGroup({ id: data.serviceGroup ? data.serviceGroup.id : '' });
    setServiceCatalog({
      id: data.serviceCatalog ? data.serviceCatalog.id : ''
    });
    setCategory({ id: data.category ? data.category.id : '' });
    setSubcategory({ id: data.subcategory ? data.subcategory.id : '' });
    setSearchTermAsset(data.asset ? data.asset.name : '');
    // Limpar erros de validação quando os dados mudarem
    setValidationErrors({
      typeTicket: false,
      priority: false,
      serviceGroup: false,
      agent: false,
      serviceCatalog: false,
      origin: false
    });
    getTypesTicket();
    getServiceGroups();
  }, [data]);

  useEffect(() => {
    getAgents();
    getServiceCatalogs();
  }, [serviceGroup.id]);

  useEffect(() => {
    disableFields();
  }, [data.status]);

  useEffect(() => {
    getAssets();
  }, [data.customer?.id, searchTermAsset]);

  const getTypesTicket = async () => {
    let response = await TypeTicketsService.list(queryParams);
    setTypesTicket(response);
  };
  const getServiceGroups = async () => {
    let response = await ServiceGroupService.list(queryParams);
    setServiceGroups(response);
  };
  const getAgents = async () => {
    if (serviceGroup.id) {
      let response = await api.serviceGroupsAgents.list(serviceGroup.id);
      setAgents(response);
    }
  };
  const getServiceCatalogs = async () => {
    if (serviceGroup.id) {
      let response = await ServiceCatalogService.listInTreeViewByServiceGroup(
        serviceGroup.id
      );
      setServiceCatalogs(response);
    }
  };
  const getAssets = async () => {
    if (!data?.customer?.id) return;

    const queryParam = new URLSearchParams({
      searchTerm: searchTermAsset
    }).toString();
    const response = await CustomerAssetsService.list(
      data.customer.id,
      queryParam
    );
    if (response && Array.isArray(response)) {
      setAssets(response);
    }
  };

  const disableFields = () => {
    const fields = form.current.elements;
    if (isDisabledForm) {
      for (let i = 0; i < fields.length; i++) {
        fields[i].disabled = true;
      }
    } else {
      for (let i = 0; i < fields.length; i++) {
        fields[i].disabled = false;
      }
    }
  };

  const handleSetBody = (hasId, fieldName, value) => {
    hasId
      ? setBody(prevBody => ({
          ...prevBody,
          [fieldName]: { id: parseInt(value) }
        }))
      : setBody(prevBody => ({ ...prevBody, [fieldName]: value }));

    // Limpar erro de validação quando o campo for preenchido
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  const handleSelectServiceCatalog = ids => {
    setServiceCatalog({ id: ids[0] });
    setCategory({ id: ids[1] });
    setSubcategory({ id: ids[2] });
    handleSetBody(true, 'serviceCatalog', ids[0]);
    handleSetBody(true, 'category', ids[1]);
    handleSetBody(true, 'subcategory', ids[2]);

    // Limpar erro de validação quando o catálogo for selecionado
    if (validationErrors.serviceCatalog && ids[0]) {
      setValidationErrors(prev => ({
        ...prev,
        serviceCatalog: false
      }));
    }
  };

  // Handle asset selection
  const handleAssetSelect = asset => {
    handleSetBody(true, 'asset', asset.id);
    setSearchTermAsset(asset.name);
    setShowAssetsSuggestions(false);
    setAssets([]);
  };

  const handleSearchTermChange = e => {
    const value = e.target.value;
    setSearchTermAsset(value);
    setShowAssetsSuggestions(value.length > 0);
  };

  const validateFields = () => {
    const errors = {
      typeTicket: !typeTicket.id || typeTicket.id === '',
      priority: !priority || priority === '',
      serviceGroup: !serviceGroup.id || serviceGroup.id === '',
      agent: !agent.id || agent.id === '',
      serviceCatalog: !serviceCatalog.id || serviceCatalog.id === '',
      origin: !origin || origin === ''
    };

    setValidationErrors(errors);

    // Abrir o painel se houver erros para que o usuário veja os campos com problema
    const hasErrors = Object.values(errors).some(error => error === true);
    if (hasErrors) {
      setShowBody(true);
    }

    // Retorna true se todos os campos estão válidos
    return !hasErrors;
  };

  // Expor função de validação para o componente pai
  useImperativeHandle(ref, () => ({
    validate: validateFields
  }));

  const onSubmit = () => {
    if (searchTermAsset.length === 0) {
      const newBody = { ...body };
      newBody.asset = null;
      handleSubmit(newBody);
      return;
    }

    handleSubmit(body);
  };

  const isDisabledForm =
    (data.status && data.status.status == 'COMPLETED') ||
    (data.status && data.status.status == 'CLOSED');
  return (
    <Card>
      <div
        className="w-100 d-flex card-header py-2 align-items-center justify-content-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setShowBody(!showBody)}
      >
        <h6 className="m-0">Propriedades</h6>
        <FontAwesomeIcon
          icon={showBody ? faChevronUp : faChevronDown}
          size="sm"
        />
      </div>
      {showBody && (
        <>
          <Card.Body>
            <Form ref={form}>
              <div className="mb-2 mt-n2">
                <Form.Label className="mb-1 fs-10">Tipo</Form.Label>
                <Form.Select
                  size="sm"
                  value={typeTicket.id}
                  onChange={e => {
                    setTypeTicket({ id: e.target.value });
                    handleSetBody(true, 'typeTicket', e.target.value);
                    if (validationErrors.typeTicket && e.target.value) {
                      setValidationErrors(prev => ({
                        ...prev,
                        typeTicket: false
                      }));
                    }
                  }}
                  className={validationErrors.typeTicket ? 'border-danger' : ''}
                >
                  <option value="">Selecione uma opção</option>
                  {typesTicket.content.map(item => (
                    <option value={item.id} key={item.id + item.name}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="mb-2">
                <Form.Label className="mb-1 mt-2 fs-10">Prioridade</Form.Label>
                <Form.Select
                  size="sm"
                  value={priority}
                  onChange={e => {
                    setPriority(e.target.value);
                    handleSetBody(false, 'priority', e.target.value);
                    if (validationErrors.priority && e.target.value) {
                      setValidationErrors(prev => ({
                        ...prev,
                        priority: false
                      }));
                    }
                  }}
                  className={validationErrors.priority ? 'border-danger' : ''}
                >
                  {priorities.map((item, i) => (
                    <option value={item.value} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div>
                <Form.Label className="mb-1 mt-2 fs-10">
                  Grupo de serviço
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={serviceGroup.id}
                  onChange={e => {
                    setServiceGroup({ id: e.target.value });
                    handleSetBody(true, 'serviceGroup', e.target.value);
                    if (validationErrors.serviceGroup && e.target.value) {
                      setValidationErrors(prev => ({
                        ...prev,
                        serviceGroup: false
                      }));
                    }
                  }}
                  className={
                    validationErrors.serviceGroup ? 'border-danger' : ''
                  }
                >
                  <option value="">Selecione uma opção</option>
                  {serviceGroups.content.map((item, i) => (
                    <option value={item.id} key={item.name + i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="mb-2">
                <Form.Label className="mb-1 mt-2 fs-10">Agente</Form.Label>
                <Form.Select
                  size="sm"
                  value={agent.id}
                  onChange={e => {
                    setAgent({ id: e.target.value });
                    handleSetBody(true, 'agent', e.target.value);
                    if (validationErrors.agent && e.target.value) {
                      setValidationErrors(prev => ({ ...prev, agent: false }));
                    }
                  }}
                  className={validationErrors.agent ? 'border-danger' : ''}
                >
                  <option value="">Selecione uma opção</option>
                  {agents.map(item => (
                    <option value={item.id} key={item.phone + item.firstName}>
                      {item.firstName}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="mb-2">
                <Form.Label className="mb-1 mt-2 fs-10">
                  Catálogo de serviço
                </Form.Label>
                <div
                  className={
                    validationErrors.serviceCatalog
                      ? 'border border-danger rounded p-1'
                      : ''
                  }
                  style={
                    validationErrors.serviceCatalog
                      ? { borderWidth: '1px !important' }
                      : {}
                  }
                >
                  <TreeSelect
                    data={serviceCatalogs}
                    onSelect={handleSelectServiceCatalog}
                    initialValue={[
                      serviceCatalog.id,
                      category.id,
                      subcategory.id
                    ]}
                    size="sm"
                    disabled={isDisabledForm}
                  />
                </div>
              </div>
              <div className="mb-2">
                <Form.Label className="mb-1 mt-2 fs-10">
                  Origem do ticket
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={origin}
                  onChange={e => {
                    setOrigin(e.target.value);
                    handleSetBody(false, 'origin', e.target.value);
                    if (validationErrors.origin && e.target.value) {
                      setValidationErrors(prev => ({ ...prev, origin: false }));
                    }
                  }}
                  className={validationErrors.origin ? 'border-danger' : ''}
                >
                  <option value="">Selecione uma opção</option>
                  {Object.entries(ticketOrigins).map(([key, value]) => (
                    <option value={key} key={key}>
                      {value}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="mb-2">
                <Form.Label className="mb-1 mt-2 fs-10">Ativo</Form.Label>
                <SearchInputWithSuggestions
                  searchTerm={searchTermAsset}
                  placeholder="Digite o nome ou o usuário do ativo..."
                  size="sm"
                  suggestions={assets}
                  showSuggestions={showAssetsSuggestions}
                  onSearchChange={handleSearchTermChange}
                  onSuggestionSelect={handleAssetSelect}
                  renderSuggestionItem={asset => (
                    <div
                      key={asset.id}
                      className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                      onClick={() => handleAssetSelect(asset)}
                      role="button"
                      style={{
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <div className="fw-bold">{asset.name}</div>
                      <div className="text-muted small">
                        {asset.dattoDevice
                          ? asset.dattoDevice.lastLoggedUser
                          : asset.assetType.name}
                      </div>
                    </div>
                  )}
                />
              </div>
            </Form>
          </Card.Body>
          <Card.Footer className="border-top border-200 py-x1">
            <Button
              varient="primary"
              className="w-100"
              onClick={() => onSubmit()}
              disabled={isDisabledForm}
            >
              Salvar
            </Button>
          </Card.Footer>
        </>
      )}
    </Card>
  );
});

PropertiesTicketForm.displayName = 'PropertiesTicketForm';

export default PropertiesTicketForm;
